import { $post } from '@/api'
import Iconfont from '@/components/iconfont/iconfont'
import { Column } from '@/components/layout/layout'
import { getQiniuImgUrl } from '@/config/qiniu'
import { IPagination } from '@/model/types/common'
import { Goods } from '@/model/types/goods'
import useBemClass from '@/utils/hooks/useBEMClass'
import { WithScrollLoad } from '@/utils/utils-widget'
import { useDynamicList, useRequest, useUpdateLayoutEffect } from 'ahooks'
import { observer } from 'mobx-react'
import React, { memo, useCallback } from 'react'
import { GoodsTableList } from '../../../goods-storage/goods-library/modules/goods-table/goods-table'
import { sortGoodStore } from '../model'
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc'
import './sort-goods-list.scss'
import { Spin } from 'antd'
import { errorCallBack } from '@/utils'
import { useEffect } from 'react'
import { useRef } from 'react'
import $msg from '@/utils/message'

const GoodItem = (props: { img_url: string; name: string; num: number }) => {
    const cls = useBemClass('sort-goods-list')

    const DragHandle = SortableHandle(() => (
        <div className={cls('item', 'left')}>
            <Iconfont name="icontuodong1" size={20} />
        </div>
    ))

    return (
        <div className={cls('item')}>
            <DragHandle />
            <div className={cls('item', 'center')}>{props.num}</div>

            <div className={cls('item', 'right')}>
                <img alt="" src={getQiniuImgUrl(props.img_url)} width="40" height="40" data-border-radius-5 />
                <div className={cls('item', 'right-txt')}>{props.name}</div>
            </div>
        </div>
    )
}

const SortGoodItem = SortableElement(GoodItem)

const SortGoodList = memo(
    SortableContainer((props: { list: Goods[]; requestHandle: any }) => {
        return (
            <WithScrollLoad data-expaned data-overflow-auto data-height-100p scrollloadhandle={props.requestHandle}>
                {props.list.map((good, i) => (
                    <SortGoodItem key={i} index={i} num={i + 1} name={good.name} img_url={good.img_url} />
                ))}
            </WithScrollLoad>
        )
    }),
)

const SortGoodsList = observer(() => {
    const cls = useBemClass('sort-goods-list')
    const maxIndex = useRef<number>(0)

    const requestHandle = useRequest<
        GoodsTableList,
        {
            goods_list: Goods[]
            pagination: IPagination
        }
    >(
        val => {
            return (
                // 在分组未完成加载前不加载表格数据
                sortGoodStore.activeId
                    ? $post({
                          url: '/goods/getgoods',
                          params: {
                              group_ids: sortGoodStore.activeId ? [sortGoodStore.activeId] : void 0,
                              secondary_group_id: sortGoodStore.secondId || void 0,
                              limit: 20,
                              page: val?.pagination?.page ? val.pagination.page + 1 : 1,
                          },
                      })
                    : new Promise(() => {})
            )
        },
        {
            isNoMore: data => {
                if (data?.pagination) {
                    return data?.pagination.page === data?.pagination.page_count
                } else {
                    return false
                }
            },
            loadMore: true,
            formatResult: data => {
                return {
                    list: data.goods_list,
                    pagination: data.pagination,
                }
            },
            refreshDeps: [sortGoodStore.activeId, sortGoodStore.secondId],
        },
    )

    const sortReq = useRequest(
        () =>
            $post({
                url: '/goods/sortgoods',
                params: {
                    goods_ids: goodListRef.current.slice(0, maxIndex.current + 1),
                },
            }),
        {
            manual: true,
            onSuccess: () => {
                $msg('success', '商品排序成功')
                sortGoodStore.setGoodUpdate()
            },
            onError: errorCallBack,
        },
    )

    const { list: goodList, resetList, move } = useDynamicList<Goods>([])
    const goodListRef = useRef<number[]>([])

    useEffect(() => {
        if (requestHandle.data) {
            resetList(requestHandle.data.list)
            goodListRef.current = requestHandle.data.list.map(g => g.id)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestHandle.data])

    useUpdateLayoutEffect(() => {
        maxIndex.current = 0
    }, [sortGoodStore.activeId, sortGoodStore.secondId])

    const moveGoodList = useCallback((oldIndex: number, newIndex: number) => {
        const newList = [...goodListRef.current]
        const temp = newList.filter((_: {}, index: number) => index !== oldIndex)
        temp.splice(newIndex, 0, newList[oldIndex])
        goodListRef.current = temp
    }, [])

    return (
        <Column>
            <div className={cls('title')}>商品</div>
            <Spin spinning={requestHandle.loadingMore || requestHandle.loading}>
                <Column className={cls('container')}>
                    <SortGoodList
                        list={goodList}
                        requestHandle={requestHandle}
                        axis="y"
                        distance={10}
                        helperClass="paymode-sort-helper"
                        onSortEnd={({ oldIndex, newIndex }) => {
                            if (oldIndex === newIndex) return
                            maxIndex.current = Math.max(oldIndex, newIndex, maxIndex.current)
                            move(oldIndex, newIndex)
                            moveGoodList(oldIndex, newIndex)
                            sortReq.run()
                        }}
                    />
                </Column>
            </Spin>
        </Column>
    )
})

export default SortGoodsList
